/**
 * NL blood-test card, told as what the customer gets rather than what we
 * hand over (AHD-14079). Experiment scoped to the Netherlands' single
 * blood-only card; every other market keeps the original layout.
 *
 * Pictures: the blood draw, then the membership-first entry screen's tubes
 * and report.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity, HeartPulse, ShieldCheck, Sun, Zap } from "lucide-react";
import { BookingLanguage } from "@/types/booking";
import { languageMap } from "@/i18n";
import imgBloodDraw from "@/assets/images/blood-draw.webp";
import imgTubes from "@/assets/images/membership-first/trio-suspended-logo-top-v2.webp";
import imgReport from "@/assets/images/membership-first/report-laptop.webp";

const PICTURES = [imgBloodDraw, imgTubes, imgReport];
const SLIDE_MS = 5000;

const BENEFITS = [1, 2, 3, 4] as const;

// The panel's areas, reusing the blood-only USP copy ("Heart health: ApoB, …")
// split at the colon into a title and its markers.
const AREAS = [
  { key: "usp.bloodOnly.heartHealth", Icon: HeartPulse },
  { key: "usp.bloodOnly.organFunction", Icon: Activity },
  { key: "usp.bloodOnly.immuneHealth", Icon: ShieldCheck },
  { key: "usp.bloodOnly.energyFatigue", Icon: Zap },
  { key: "usp.bloodOnly.nutrientBalance", Icon: Sun },
] as const;

const useT = (language: BookingLanguage) =>
  useTranslation(undefined, { lng: languageMap[language] }).t;

export const ValueBenefits = ({ language }: { language: BookingLanguage }) => {
  const t = useT(language);
  return (
    <ul className="mt-3 flex flex-col gap-1.5">
      {BENEFITS.map((i) => (
        <li key={i} className="flex items-start gap-2">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            className="mt-0.5 flex-shrink-0"
          >
            <circle cx="8" cy="8" r="7.5" className="fill-deep-green-600" />
            <path
              d="M5 8l2 2 4-4"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-[14px] leading-[20px] text-neutral-900">
            {t(`valueFraming.benefits.${i}`)}
          </span>
        </li>
      ))}
    </ul>
  );
};

export const ValueCarousel = ({ language }: { language: BookingLanguage }) => {
  const t = useT(language);
  const [slide, setSlide] = useState(0);
  // Keyed on slide so a dot click restarts the timer.
  useEffect(() => {
    const id = window.setTimeout(
      () => setSlide((s) => (s + 1) % PICTURES.length),
      SLIDE_MS,
    );
    return () => window.clearTimeout(id);
  }, [slide]);

  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-neutral-100 lg:aspect-auto lg:h-full">
      {PICTURES.map((img, i) => (
        <img
          key={img}
          src={img}
          alt=""
          aria-hidden="true"
          loading={i === 0 ? "eager" : "lazy"}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
            i === slide ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
        {PICTURES.map((img, i) => (
          <button
            key={img}
            type="button"
            aria-label={t("valueFraming.slide", { n: i + 1 })}
            aria-current={i === slide}
            onClick={(e) => {
              // The card selects on click on mobile.
              e.stopPropagation();
              setSlide(i);
            }}
            className={`h-1.5 rounded-full transition-all ${
              i === slide ? "w-4 bg-white" : "w-1.5 bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export const ValueAreas = ({ language }: { language: BookingLanguage }) => {
  const t = useT(language);
  return (
    <section className="flex flex-col gap-3">
      <h4 className="px-1 text-[16px] font-medium text-neutral-900">
        {t("valueFraming.areasTitle")}
      </h4>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {AREAS.map(({ key, Icon }) => {
          const [title, markers] = t(key).split(/:\s*/);
          return (
            <div
              key={key}
              className="flex flex-col gap-1 rounded-xl border border-white p-3 shadow-card"
              style={{ background: "rgba(255, 255, 255, 0.95)" }}
            >
              <Icon
                className="size-4 text-aubergine-800"
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <p className="mt-1 text-[13px] font-medium leading-tight text-neutral-900">
                {title}
              </p>
              {markers && (
                <p className="text-[12px] leading-tight text-neutral-500">
                  {markers}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
